import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme as useSiteTheme } from '@/services/ThemeContext';
import { useSubscription, hasFeature } from '@/services/subscription';
import { TeamDashboardProvider } from './TeamDashboardContext';
import { useTeamDashboard } from './TeamDashboardContext';
import Sidebar from './layout/Sidebar';
import Topbar from './layout/Topbar';
import './teamDashboard.css';

import OverviewPage from './pages/OverviewPage';
import TeamPage from './pages/TeamPage';
import BudgetPage from './pages/BudgetPage';
import QualityPage from './pages/QualityPage';
import ProjectsPage from './pages/ProjectsPage';
import InvoicesPage from './pages/InvoicesPage';
import DevModePage from './pages/DevModePage';
import ReportsPage from './pages/ReportsPage';
import WorkClockPage from './pages/WorkClockPage';
import WcShiftsPage from './pages/WcShiftsPage';
import WcCommitsPage from './pages/WcCommitsPage';
import WcBudgetPage from './pages/WcBudgetPage';
import DiscussionPage from './pages/DiscussionPage';
import SettingsPage from './pages/SettingsPage';
import AiAssistantModal from './components/AiAssistantModal';

const teamDashboardStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Scrudix Team Dashboard',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: 'https://scrudix.com/team-dashboard',
  description:
    'Scrudix Team Dashboard helps engineering and product teams track projects, budgets, quality, work clocks, delivery signals, and collaboration in one shared workspace.',
  featureList: [
    'Team dashboard software for engineering and product teams',
    'Shared project, budget, and quality reporting',
    'Work clock, shift tracking, and commit visibility',
    'Collaboration views for discussions and delivery reporting',
  ],
};



function TeamDashboardSeoIntro() {
  return null;
}

function Shell() {
  const { activePage, theme, toastState, sidebarCollapsed, workspaceAccessLocked, workspaceAccessMessage, isAiOpen, setAiOpen, members, budget, projects, isReal } = useTeamDashboard();
  const subscription = useSubscription();
  const isProPlusTeam = hasFeature(subscription.plan, 'teamDashboard');
  const isFreeOrPro = !isProPlusTeam;

  const dashboardData = {
    hoursLogged: isReal ? members.reduce((sum, m) => sum + Number(m.hoursThisWeek || 0), 0) : 34.5,
    spent: isReal ? Math.max(0, Number(budget?.spent ?? 0)) : 17500,
    remainingBudget: isReal ? Math.max(0, Number(budget?.remaining ?? 0)) : 32500,
    totalBudget: isReal ? Math.max(0, Number(budget?.total ?? 0)) : 50000,
    activeMembers: isReal ? members.length : 4,
    activeProjects: isReal ? projects.filter(p => p.status === 'active' || p.status === 'review').length : 2,
    qualityAvg: isReal && members.length > 0
      ? Math.round(members.reduce((sum, m) => sum + Number(m.qualityScore || 0), 0) / members.length)
      : 76,
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      const isEditing = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        (activeEl as HTMLElement).isContentEditable
      );
      if (isEditing) return;

      // If active element is inside an inner scrollable panel (like AI chat feed or sidebar), let it handle its own scroll
      if (activeEl && (activeEl.closest('.pulse-ai-feed') || activeEl.closest('.pulse-ai-rail-card') || activeEl.closest('.sb-nav'))) {
        return;
      }

      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const step = 140;
      const pageStep = container.clientHeight * 0.85;

      const canScrollContainer = container.scrollHeight > container.clientHeight;
      if (canScrollContainer) {
        if (e.key === 'ArrowDown') {
          container.scrollTop += step;
        } else if (e.key === 'ArrowUp') {
          container.scrollTop -= step;
        } else if (e.key === 'PageDown' || (e.key === ' ' && !e.shiftKey)) {
          e.preventDefault();
          container.scrollTop += pageStep;
        } else if (e.key === 'PageUp' || (e.key === ' ' && e.shiftKey)) {
          e.preventDefault();
          container.scrollTop -= pageStep;
        } else if (e.key === 'Home') {
          e.preventDefault();
          container.scrollTop = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          container.scrollTop = container.scrollHeight;
        }
      } else {
        // Fallback to native window scroll
        if (e.key === 'ArrowDown') {
          window.scrollBy({ top: step, behavior: 'smooth' });
        } else if (e.key === 'ArrowUp') {
          window.scrollBy({ top: -step, behavior: 'smooth' });
        } else if (e.key === 'PageDown' || (e.key === ' ' && !e.shiftKey)) {
          e.preventDefault();
          window.scrollBy({ top: window.innerHeight * 0.85, behavior: 'smooth' });
        } else if (e.key === 'PageUp' || (e.key === ' ' && e.shiftKey)) {
          e.preventDefault();
          window.scrollBy({ top: -window.innerHeight * 0.85, behavior: 'smooth' });
        } else if (e.key === 'Home') {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (e.key === 'End') {
          e.preventDefault();
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Cursor Auto-Scroll for Team Dashboard Workspace (No manual scrollbar required)
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let animId: number | null = null;
    let velocity = 0;

    const loop = () => {
      if (velocity !== 0 && el) {
        el.scrollTop += velocity;
        animId = requestAnimationFrame(loop);
      } else {
        animId = null;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      // Do not auto-scroll outer view when cursor is inside inner scrollable panels like AI chat
      if (target && target.closest('.pulse-ai-feed, .pulse-ai-rail-card, .sb-nav, input, textarea')) {
        velocity = 0;
        return;
      }

      const rect = el.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const edge = 50;
      const maxSpeed = 16;

      if (y >= 0 && y < edge) {
        const ratio = (edge - y) / edge;
        velocity = -Math.max(2, Math.round(ratio * maxSpeed));
        if (!animId) animId = requestAnimationFrame(loop);
      } else if (y > rect.height - edge && y <= rect.height) {
        const ratio = (y - (rect.height - edge)) / edge;
        velocity = Math.max(2, Math.round(ratio * maxSpeed));
        if (!animId) animId = requestAnimationFrame(loop);
      } else {
        velocity = 0;
      }
    };

    const handleMouseLeave = () => {
      velocity = 0;
      if (animId) {
        cancelAnimationFrame(animId);
        animId = null;
      }
    };

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
      if (animId) cancelAnimationFrame(animId);
    };
  }, []);

  function renderPage() {
    return (
      <>
        {activePage === 'overview' && <OverviewPage />}
        {activePage === 'team' && <TeamPage />}
        {activePage === 'budget' && <BudgetPage />}
        {activePage === 'quality' && <QualityPage />}
        {activePage === 'projects' && <ProjectsPage />}
        {activePage === 'invoices' && <InvoicesPage />}
        {activePage === 'devmode' && <DevModePage />}
        {activePage === 'reports' && <ReportsPage />}
        {activePage === 'workclock' && <WorkClockPage />}
        {activePage === 'wcshifts' && <WcShiftsPage />}
        {activePage === 'wccommits' && <WcCommitsPage />}
        {activePage === 'wcbudget' && <WcBudgetPage />}
        {activePage === 'discussion' && <DiscussionPage />}
        {activePage === 'settings' && <SettingsPage />}
      </>
    );
  }

  // Free/Pro users get limited access: preview team dashboard with 2-member limit
  // Pro+ users get full access



  if (workspaceAccessLocked) {
    return (
      <div data-theme={theme} className="td-shell td-shell-locked">
        <div className="td-locked-panel">
          <div className="td-locked-badge">Protected Workspace</div>
          <h1 className="td-locked-title">Only the workspace owner can open this team area.</h1>
          <p className="td-locked-copy">
            {workspaceAccessMessage || 'This team dashboard is locked down so collaborators cannot browse code-linked projects, commit feeds, budgets, or team telemetry.'}
          </p>

          <div className="td-locked-grid">
            <div className="td-locked-card">
              <div className="td-locked-card-title">Repos and commits</div>
              <div className="td-locked-card-copy">Hidden from collaborator accounts to reduce code leakage risk.</div>
            </div>
            <div className="td-locked-card">
              <div className="td-locked-card-title">Budgets and rates</div>
              <div className="td-locked-card-copy">Owner-only visibility for spend, rates, invoices, and usage telemetry.</div>
            </div>
            <div className="td-locked-card">
              <div className="td-locked-card-title">Workspace telemetry</div>
              <div className="td-locked-card-copy">Activity feeds, quality data, and work-clock signals stay protected.</div>
            </div>
          </div>

          <div className="td-locked-actions">
            <button className="td-locked-btn td-locked-btn-primary" onClick={() => window.location.reload()}>
              Retry access
            </button>
            <button className="td-locked-btn" onClick={() => window.location.assign('/dashboard')}>
              Personal dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-theme={theme} className="td-shell" style={{ position: 'relative' }}>
      {/* Floating Ambient Neon Glow Blobs (Obsidian Glass Aesthetic) */}
      <div className="absolute top-[10%] left-[20%] w-[350px] h-[350px] rounded-full bg-indigo-500/10 dark:bg-indigo-500/[0.04] blur-[100px] pointer-events-none z-0" />
      <div className="absolute bottom-[10%] right-[15%] w-[350px] h-[350px] rounded-full bg-cyan-500/10 dark:bg-cyan-500/[0.04] blur-[100px] pointer-events-none z-0" />
      <div className="absolute top-[50%] left-[55%] w-[300px] h-[300px] rounded-full bg-purple-500/10 dark:bg-purple-500/[0.04] blur-[90px] pointer-events-none z-0" />
      <Sidebar />
      <div className={`td-main${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <Topbar />
        <div
          ref={scrollRef}
          tabIndex={0}
          style={{ outline: 'none' }}
          className={`td-scroll${activePage === 'discussion' ? ' disc-active' : ''}`}
        >
          {activePage === 'overview' && <TeamDashboardSeoIntro />}
          <AnimatePresence mode="wait">
            <motion.div
              key={activePage}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.18, ease: 'easeInOut' }}
              className="w-full min-h-full"
              style={activePage === 'discussion' ? { height: '100%', display: 'flex', flexDirection: 'column', flex: 1 } : undefined}
            >
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Toast */}
      {toastState.visible && (
        <div className={`td-toast show toast-${toastState.type}`}>
          <div className="toast-title">{toastState.title}</div>
          {toastState.sub && <div className="toast-sub">{toastState.sub}</div>}
        </div>
      )}

      <AnimatePresence>
        {isAiOpen && (
          <AiAssistantModal
            onClose={() => setAiOpen(false)}
            dashboardData={dashboardData}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default function TeamDashboard() {
  const { theme: siteTheme, setTheme: setSiteTheme } = useSiteTheme();

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(teamDashboardStructuredData) }}
      />
      <TeamDashboardProvider themeOverride={siteTheme} onThemeChange={setSiteTheme}>
        <Shell />
      </TeamDashboardProvider>
    </>
  );
}
